/**
 * 已发布绘本的阅读器加载器。
 * 职责：拉取并解密 project-config / ui.json，应用 UI 覆盖项，注册字体，写入 projectStore。
 * 注意：不依赖编辑器解析、写回与持久化逻辑，供阅读页与预览入口复用。
 */
import { DEFAULT_ENABLE_WATERMARK } from '@/constants/canvasConstants';
import { useFontStore } from '@/store/font';
import { useProjectStore } from '@/store/project';
import type { FontInfo } from '@/types/font-type';
import type { ProjectConfigData, UIConfigData } from '@/types/online-data';
import { syncPageLayoutDimensions } from '@/utils/canvasUtil';
import { decodeBase64, decryptProjectConfig } from '@/utils/decrypt';
import { resolveProjectPaths } from '@/utils/projectUtil';
import { debugCheckpoint } from '@/utils/mobileDebugConsole';
import { resolveAppStaticPath } from '@/utils/staticAsset';

/**
 * 从原始文本中提取加密配置载荷。
 * 兼容两种形态：纯密文字符串，或 `{ config: "<密文>" }` JSON 包装。
 */
function extractEncryptedConfig(rawText: string): string {
  const trimmed = rawText.trim();
  if (!trimmed.startsWith('{')) return trimmed;

  try {
    const parsed = JSON.parse(trimmed) as { config?: unknown };
    if (typeof parsed.config === 'string') return parsed.config;
  } catch {
    // 非 JSON 时按纯密文继续处理
  }

  return trimmed;
}

/**
 * 拉取阅读器旁路 UI 配置（../ui.json）。
 * 失败时返回 null，由调用方回退到 project 内默认值。
 */
async function fetchReaderUiConfig(): Promise<UIConfigData | null> {
  try {
    const response = await fetch('../ui.json');
    if (!response.ok) return null;

    const content = extractEncryptedConfig(await response.text());
    return JSON.parse(decodeBase64(content)) as UIConfigData;
  } catch {
    return null;
  }
}

/**
 * 拉取并解密已发布的 project-config.json。
 * 优先使用 HTML 中 `#project-config` preload 链接，否则回退到相对路径。
 */
async function fetchEncryptedProjectConfig(): Promise<ProjectConfigData> {
  const preloadLink = document.getElementById('project-config') as HTMLLinkElement | null;
  const configUrl = preloadLink?.getAttribute('href') || './project-config.json';
  const response = await fetch(configUrl);

  if (!response.ok) {
    throw new Error(`加载 project-config.json 失败: ${response.status} (${configUrl})`);
  }

  const content = extractEncryptedConfig(await response.text());
  return JSON.parse(await decryptProjectConfig(content)) as ProjectConfigData;
}

/**
 * 并行拉取已发布工程配置与阅读器 UI 配置。
 */
export async function fetchPublishedProjectConfig(): Promise<{
  data: ProjectConfigData;
  uiConfig: UIConfigData | null;
}> {
  const [data, uiConfig] = await Promise.all([
    fetchEncryptedProjectConfig(),
    fetchReaderUiConfig()
  ]);
  return { data, uiConfig };
}

/**
 * 将 ui.json 中的运营/展示字段覆盖到工程数据。
 * - isFree：由 pv === 0 判定
 * - enable_watermark：优先用 ui 配置，否则补默认值
 */
export function applyPublishedUiConfig(
  data: ProjectConfigData,
  uiConfig: UIConfigData | null
): void {
  if (uiConfig) {
    data.isFree = uiConfig.pv === 0;
    if (data.project) {
      data.project.enable_watermark = uiConfig.enable_watermark ?? DEFAULT_ENABLE_WATERMARK;
    }
    return;
  }

  if (data.project) data.project.enable_watermark ??= DEFAULT_ENABLE_WATERMARK;
}

/**
 * 注册阅读器所需字体到 document.fonts，并同步到 fontStore。
 * 路径优先级：reader 专用路径 > app 静态路径 > 工程相对路径。
 */
async function registerReaderFonts(fonts: FontInfo[]): Promise<void> {
  const fontStore = useFontStore();
  await Promise.all(
    fonts.map(async (font) => {
      if (!font?.fontFamily || !font.path || fontStore.registerFonts.has(font.fontFamily)) return;

      const path =
        font.apply === 'reader' && font.readerPath
          ? resolveAppStaticPath(font.readerPath)
          : font.type === 'app'
            ? resolveAppStaticPath(font.path)
            : font.path;

      try {
        const loadedFace = await new FontFace(font.fontFamily, `url('${path}')`).load();
        document.fonts.add(loadedFace);
        fontStore.registerFonts.set(font.fontFamily, font);
      } catch (error) {
        console.error(`字体:${font.label || font.fontFamily} 注册失败`, { error });
      }
    })
  );
}

/**
 * 加载已发布绘本到阅读器：解密配置 → 应用 UI → 解析路径/布局 → 注册字体 → 写入 store。
 * 不引入编辑器解析或持久化代码，保证阅读包体积与职责隔离。
 */
export async function loadPublishedReaderProject(): Promise<ProjectConfigData> {
  // 1. 并行拉取工程配置与 UI 配置
  const { data, uiConfig } = await fetchPublishedProjectConfig();

  if (!data.project) {
    throw new Error('project-config.json 缺少已发布的 project 数据');
  }

  // 2. 用 ui.json 覆盖免费态、水印等展示字段
  applyPublishedUiConfig(data, uiConfig);

  // 3. 补齐工程 id，解析资源路径，同步页布局尺寸
  data.project.id = data.id;
  resolveProjectPaths(data.project, data.project.baseUrl);
  syncPageLayoutDimensions(data.project);

  // 4. 注册工程字体，避免阅读时缺字
  await registerReaderFonts(data.project.fonts ?? []);

  // 5. 重置并写入 projectStore，供阅读器渲染使用
  const projectStore = useProjectStore();
  projectStore.reset();
  await projectStore.load(data.project);
  projectStore.isFree = data.isFree;

  debugCheckpoint('project', 'readerProjectConfig:done', {
    hasProject: true,
    projectId: data.id
  });

  return data;
}
